// Actual (expended) hours at discipline level with breakdown allocation (rule 12, X01, E08) and indirect items (rule 20).
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Doc } from "../../../convex/_generated/dataModel";
import { BD_LABEL, useProject } from "@/hooks/project";
import { Badge, Button, Card, Checkbox, Field, Input, Modal, PageHeader, Select, Tabs, fmtDate, fmtNum, useRun, useToast } from "@/components/ui";
import { DataGrid, type Col } from "@/components/DataGrid";
import { ExportButton, ImportButton } from "@/components/Importer";
import { exportActuals, parseActuals } from "@/lib/formats";
import { compareKeys, spreadIndirect } from "@/engine/compute";
import { sortedPeriods } from "@/shared/model";
import type { Codes } from "@/engine/types";

const ALLOC = ["dbs", "wbs", "obs", "pbs"] as const;
interface Line {
  key: string;
  codes: Codes;
  indirect: boolean;
  byPeriod: Map<number, number>;
}

export function ActualsPage() {
  const [tab, setTab] = useState<"enter" | "history" | "indirect">("enter");
  return (
    <div className="space-y-4">
      <PageHeader title="Actual hours" sub="Actual hours are entered per period at discipline (DBS) level, optionally split by WBS, OBS and PBS. Never per deliverable." />
      <Tabs value={tab} onChange={setTab} items={[{ value: "enter", label: "Enter hours" }, { value: "history", label: "By period" }, { value: "indirect", label: "Indirect items" }]} />
      {tab === "enter" && <Enter />}
      {tab === "history" && <History />}
      {tab === "indirect" && <Indirects />}
    </div>
  );
}

function useLines(extra: Line[] = []) {
  const { b, data } = useProject();
  return useMemo(() => {
    const m = new Map<string, Line>();
    const key = (c: Codes, ind: boolean) => `${ind ? 1 : 0}|${ALLOC.map((k) => c[k] ?? "").join("|")}`;
    for (const a of b.actuals) {
      const k = key(a.codes, a.indirect);
      const l = m.get(k) ?? { key: k, codes: a.codes, indirect: a.indirect, byPeriod: new Map() };
      l.byPeriod.set(a.periodNumber, (l.byPeriod.get(a.periodNumber) ?? 0) + a.hours);
      m.set(k, l);
    }
    for (const e of extra) if (!m.has(e.key)) m.set(e.key, e);
    const codes = (l: Line) => ALLOC.map((k) => l.codes[k] ?? "");
    return [...m.values()].sort((x, y) => Number(x.indirect) - Number(y.indirect) || compareKeys(data.codeOrder, [...ALLOC], codes(x), codes(y)));
  }, [b.actuals, extra, data.codeOrder]);
}

function Enter() {
  const { project, periods, currentIdx, canEdit, nodes, activeBreakdowns } = useProject();
  const setHours = useMutation(api.actuals.setHours);
  const run = useRun();
  const toast = useToast();
  const [pn, setPn] = useState<number>(periods[currentIdx]?.number ?? 1);
  const [extra, setExtra] = useState<Line[]>([]);
  const [add, setAdd] = useState<{ codes: Codes; indirect: boolean } | null>(null);
  const lines = useLines(extra);
  const period = periods.find((p) => p.number === pn);
  const alloc = ALLOC.filter((k) => activeBreakdowns.includes(k));
  const cols: Col<Line>[] = [
    ...alloc.map((k) => ({ id: k, title: BD_LABEL[k], width: 90, get: (r: Line) => r.codes[k] })),
    { id: "ind", title: "Indirect", width: 70, kind: "bool" as const, get: (r: Line) => r.indirect },
    {
      id: "h",
      title: `Hours P${pn}`,
      width: 110,
      kind: "number",
      digits: 1,
      get: (r) => r.byPeriod.get(pn) ?? null,
      set: canEdit ? () => {} : undefined,
      readonly: () => !!period?.closed,
      tone: () => (period?.closed ? "muted" : undefined),
    },
    { id: "cum", title: "Cumulative", width: 100, kind: "number", digits: 1, get: (r) => [...r.byPeriod.entries()].filter(([n]) => n <= pn).reduce((s, [, v]) => s + v, 0) },
  ];
  const total = lines.reduce((s, l) => s + (l.byPeriod.get(pn) ?? 0), 0);
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          Period
          <Select value={pn} onChange={(e) => setPn(Number(e.target.value))} className="h-8 w-64" aria-label="Period">
            {periods.map((p) => (
              <option key={p.number} value={p.number}>
                P{p.number} · {fmtDate(p.start)} to {fmtDate(p.cutoff)} {p.closed ? "(closed)" : ""}
              </option>
            ))}
          </Select>
          {period?.closed && <Badge>closed</Badge>}
        </span>
      }
      actions={
        <>
          <span className="text-sm text-slate-600">Total {fmtNum(total, 1)} h</span>
          {canEdit && !period?.closed && (
            <Button onClick={() => setAdd({ codes: {}, indirect: false })}>
              <Plus className="size-4" /> Add line
            </Button>
          )}
        </>
      }
    >
      {!periods.length ? (
        <p className="text-sm text-slate-500">Set up the calendar first.</p>
      ) : (
        <DataGrid
          rows={lines}
          cols={cols}
          height={460}
          freeze={0}
          onEdit={(edits) =>
            void run(() => setHours({ projectId: project._id, entries: edits.map((e) => ({ periodNumber: pn, codes: e.row.codes, indirect: e.row.indirect, hours: e.value === null ? 0 : Number(e.value) })) }))
          }
          onError={(m) => toast({ text: m, tone: "error" })}
        />
      )}
      {add && (
        <Modal
          open
          onClose={() => setAdd(null)}
          title="Add an allocation line"
          footer={
            <>
              <Button onClick={() => setAdd(null)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={!add.codes.dbs}
                onClick={() => {
                  const codes = Object.fromEntries(Object.entries(add.codes).filter(([, v]) => v)) as Codes;
                  setExtra((x) => [...x, { key: `${add.indirect ? 1 : 0}|${ALLOC.map((k) => codes[k] ?? "").join("|")}`, codes, indirect: add.indirect, byPeriod: new Map() }]);
                  setAdd(null);
                }}
              >
                Add
              </Button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            {alloc.map((k) => (
              <Field key={k} label={`${BD_LABEL[k]}${k === "dbs" ? " (required)" : ""}`}>
                <Select value={add.codes[k] ?? ""} onChange={(e) => setAdd({ ...add, codes: { ...add.codes, [k]: e.target.value } })}>
                  <option value="" />
                  {nodes[k].map((n) => (
                    <option key={n.code} value={n.code}>
                      {n.code} · {n.title}
                    </option>
                  ))}
                </Select>
              </Field>
            ))}
            <Checkbox label="Indirect (non-progressable) hours" checked={add.indirect} onChange={(v) => setAdd({ ...add, indirect: v })} />
          </div>
        </Modal>
      )}
    </Card>
  );
}

function History() {
  const { project, periods, b, canEdit } = useProject();
  const setHours = useMutation(api.actuals.setHours);
  const lines = useLines();
  const shown = periods.filter((p) => b.actuals.some((a) => a.periodNumber === p.number) || !p.closed).slice(0, 60);
  return (
    <Card
      title="Hours by period"
      actions={
        <>
          <ExportButton file={`${project.code}-actual-hours.xlsx`} sheets={() => [exportActuals(b.actuals.map((a) => ({ periodNumber: a.periodNumber, codes: a.codes, hours: a.hours, indirect: a.indirect })))]} />
          {canEdit && (
            <ImportButton
              build={(sheets) => {
                const { entries, errors } = parseActuals(sheets[0].rows, sortedPeriods(periods));
                const closed = new Set(periods.filter((p) => p.closed).map((p) => p.number));
                return {
                  problems: errors.map((e) => `Row ${e.line}: ${e.message}`),
                  note: <p className="text-sm text-slate-600">Each row sets the hours for that period and allocation (it replaces, not adds). Closed periods are rejected.</p>,
                  rows: entries.map((e, i) => ({
                    line: i + 1,
                    key: `P${e.periodNumber} ${ALLOC.map((k) => e.codes[k]).filter(Boolean).join(" / ")}${e.indirect ? " (indirect)" : ""}`,
                    status: closed.has(e.periodNumber) ? "error" : "changed",
                    detail: closed.has(e.periodNumber) ? "Period is closed" : `${fmtNum(e.hours, 1)} h`,
                  })),
                  commit: () => setHours({ projectId: project._id, entries }),
                };
              }}
            />
          )}
        </>
      }
    >
      <div className="overflow-x-auto">
        <table className="text-sm">
          <thead className="text-xs text-slate-500">
            <tr>
              <th className="sticky left-0 bg-surface px-2 py-1 text-left">Allocation</th>
              {shown.map((p) => (
                <th key={p.number} className="px-2 py-1 text-right">
                  P{p.number}
                  <div className="font-normal">{fmtDate(p.cutoff)}</div>
                </th>
              ))}
              <th className="px-2 py-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key} className="border-t border-slate-100">
                <td className="sticky left-0 whitespace-nowrap bg-surface px-2 py-1">
                  {ALLOC.map((k) => l.codes[k]).filter(Boolean).join(" / ")} {l.indirect && <Badge tone="violet">indirect</Badge>}
                </td>
                {shown.map((p) => (
                  <td key={p.number} className="num px-2 py-1 text-right">
                    {l.byPeriod.get(p.number) ? fmtNum(l.byPeriod.get(p.number), 1) : ""}
                  </td>
                ))}
                <td className="num px-2 py-1 text-right font-medium">{fmtNum([...l.byPeriod.values()].reduce((s, v) => s + v, 0), 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Indirects() {
  const { project, b, periods, canEdit, nodes, activeBreakdowns } = useProject();
  const save = useMutation(api.actuals.saveIndirect);
  const del = useMutation(api.actuals.deleteIndirect);
  const run = useRun();
  const [edit, setEdit] = useState<Partial<Doc<"indirects">> | null>(null);
  const ps = sortedPeriods(periods);
  return (
    <Card title="Indirect / non-progressable items" actions={canEdit && !project.baselineLocked && <Button variant="primary" onClick={() => setEdit({ codes: {} })}><Plus className="size-4" /> Add</Button>}>
      <p className="mb-3 text-xs text-slate-500">
        Budget spread evenly per period between start and end. They can't be earned through milestones. They stay off the progress curves (the curve report can add them as a flat layer) and join CPI/EAC only when the project setting
        includes them.
      </p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-slate-500">
          <tr>
            <th className="py-1">Item</th>
            <th>Codes</th>
            <th className="text-right">Budget</th>
            <th>Start</th>
            <th>End</th>
            <th className="text-right">Per period</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {b.indirects.map((i) => {
            const spread = spreadIndirect({ id: i._id, budget: i.budget, start: i.start, end: i.end, codes: i.codes }, ps);
            const n = spread.filter((x) => x > 0).length;
            return (
              <tr key={i._id} className="border-t border-slate-100">
                <td className="py-1.5 font-medium">{i.title}</td>
                <td>{Object.values(i.codes).filter(Boolean).join(" / ")}</td>
                <td className="num text-right">{fmtNum(i.budget)}</td>
                <td>{fmtDate(i.start)}</td>
                <td>{fmtDate(i.end)}</td>
                <td className="num text-right">
                  {n ? fmtNum(i.budget / n, 1) : "–"} <span className="text-xs text-slate-500">× {n}</span>
                </td>
                <td className="text-right">
                  {canEdit && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEdit(i)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" aria-label="Delete" disabled={project.baselineLocked} onClick={() => run(() => del({ id: i._id }))}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!b.indirects.length && <p className="py-4 text-center text-sm text-slate-500">No indirect items.</p>}
      {edit && (
        <Modal
          open
          onClose={() => setEdit(null)}
          title={edit._id ? "Edit indirect item" : "New indirect item"}
          footer={
            <>
              <Button onClick={() => setEdit(null)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={async () => {
                  const ok = await run(() =>
                    save({ projectId: project._id, id: edit._id, title: edit.title ?? "", budget: Number(edit.budget ?? 0), start: edit.start ?? "", end: edit.end ?? "", codes: Object.fromEntries(Object.entries(edit.codes ?? {}).filter(([, v]) => v)) }),
                  );
                  if (ok !== undefined) setEdit(null);
                }}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title" className="col-span-2">
              <Input value={edit.title ?? ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
            </Field>
            <Field label="Budget hours">
              <Input type="number" min={0} value={edit.budget ?? ""} onChange={(e) => setEdit({ ...edit, budget: Number(e.target.value) })} />
            </Field>
            <div />
            <Field label="Start">
              <Input type="date" value={edit.start ?? ""} onChange={(e) => setEdit({ ...edit, start: e.target.value })} />
            </Field>
            <Field label="End">
              <Input type="date" value={edit.end ?? ""} onChange={(e) => setEdit({ ...edit, end: e.target.value })} />
            </Field>
            {ALLOC.filter((k) => activeBreakdowns.includes(k)).map((k) => (
              <Field key={k} label={BD_LABEL[k]}>
                <Select value={edit.codes?.[k] ?? ""} onChange={(e) => setEdit({ ...edit, codes: { ...(edit.codes ?? {}), [k]: e.target.value } })}>
                  <option value="" />
                  {nodes[k].map((n) => (
                    <option key={n.code} value={n.code}>
                      {n.code} · {n.title}
                    </option>
                  ))}
                </Select>
              </Field>
            ))}
          </div>
        </Modal>
      )}
    </Card>
  );
}
